import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: "Quick Links",
      links: [
        { name: "Menu", path: "/interactive-menu-culinary-discovery" },
        { name: "Order Online", path: "/order-online-streamlined-experience" },
        { name: "Catering", path: "/catering-services-event-solutions" },
        { name: "Our Story", path: "/our-story-heritage-authenticity" },
        { name: "Contact", path: "/contact-location-welcoming-connection" }
      ]
    },
    {
      title: "Customer Care",
      links: [
        { name: "FAQ", path: "#" },
        { name: "Delivery Info", path: "#" },
        { name: "Nutrition Guide", path: "#" },
        { name: "Allergen Information", path: "#" },
        { name: "Gift Cards", path: "#" }
      ]
    },
    {
      title: "Community",
      links: [
        { name: "Cooking Classes", path: "#" },
        { name: "Events Calendar", path: "#" },
        { name: "Recipe Sharing", path: "#" },
        { name: "Customer Stories", path: "#" },
        { name: "Newsletter", path: "#" }
      ]
    }
  ];

  const socialLinks = [
    { name: "Facebook", icon: "Facebook", url: "#", color: "hover:text-blue-500" },
    { name: "Instagram", icon: "Instagram", url: "#", color: "hover:text-pink-500" },
    { name: "Twitter", icon: "Twitter", url: "#", color: "hover:text-blue-400" },
    { name: "YouTube", icon: "Youtube", url: "#", color: "hover:text-red-500" },
    { name: "TikTok", icon: "Music", url: "#", color: "hover:text-purple-500" }
  ];

  const contactInfo = [
    { icon: "MapPin", text: "123 Mediterranean Ave, Foodie District, FC 12345" },
    { icon: "Phone", text: "(555) 123-KEBAB" },
    { icon: "Mail", text: "hello@kebabpalace.com" },
    { icon: "Clock", text: "Mon-Sun: 11:00 AM - 10:00 PM" }
  ];

  const certifications = [
    { name: "Halal Certified", icon: "Shield" },
    { name: "Fresh Guarantee", icon: "Leaf" },
    { name: "Local Sourcing", icon: "MapPin" },
    { name: "Family Owned", icon: "Heart" }
  ];

  return (
    <footer className="bg-text-primary text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/homepage-mediterranean-restaurant-experience" className="flex items-center space-x-3 mb-6">
              <div className="relative">
                <svg width="48" height="48" viewBox="0 0 48 48">
                  <circle cx="24" cy="24" r="22" fill="url(#footerLogoGradient)" stroke="#DAA520" strokeWidth="2"/>
                  <path d="M16 20 L24 14 L32 20 L28 28 L20 28 Z" fill="#DAA520" opacity="0.9"/>
                  <circle cx="24" cy="22" r="3" fill="#8B4513"/>
                  <defs>
                    <linearGradient id="footerLogoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#C4622D"/>
                      <stop offset="100%" stopColor="#8B4513"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div>
                <h3 className="font-headline text-2xl font-bold text-white">Kebab Palace</h3>
                <p className="font-accent text-sm text-white/80">Family Recipe Since 1987</p>
              </div>
            </Link>
            
            <p className="text-white/80 leading-relaxed mb-6 max-w-md">
              Bringing authentic Mediterranean flavors to your table with traditional recipes, fresh ingredients, and the warmth of family hospitality. Every meal is a celebration of our culinary heritage.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              {contactInfo.map((info, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <Icon name={info.icon} size={18} className="text-accent mt-1 flex-shrink-0" />
                  <span className="text-white/80 text-sm">{info.text}</span>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  className={`w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white/80 transition-organic ${social.color}`}
                  aria-label={social.name}
                >
                  <Icon name={social.icon} size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {footerSections.map((section, index) => (
            <div key={index}>
              <h4 className="font-cta font-bold text-lg text-white mb-4">
                {section.title}
              </h4>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      to={link.path}
                      className="text-white/80 hover:text-accent transition-organic text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Certifications */}
        <div className="border-t border-white/20 mt-12 pt-8">
          <h4 className="font-cta font-bold text-lg text-white mb-4 text-center">
            Our Commitments
          </h4>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {certifications.map((cert, index) => (
              <div key={index} className="flex items-center space-x-2 justify-center lg:justify-start">
                <div className="w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center">
                  <Icon name={cert.icon} size={16} className="text-accent" />
                </div>
                <span className="text-white/80 text-sm font-medium">{cert.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="border-t border-white/20 mt-8 pt-8">
          <div className="max-w-md mx-auto text-center">
            <h4 className="font-cta font-bold text-lg text-white mb-2">
              Stay in the Loop
            </h4>
            <p className="text-white/80 text-sm mb-4">
              Get the latest on new dishes, events, and special offers
            </p>
            <div className="flex space-x-2">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-1 px-4 py-2 rounded-lg bg-white/10 text-white placeholder-white/60 border border-white/20 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              />
              <button className="bg-accent hover:bg-accent-600 text-white px-6 py-2 rounded-lg font-cta font-semibold transition-organic">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col lg:flex-row items-center space-y-2 lg:space-y-0 lg:space-x-6 text-sm text-white/60">
              <span>&copy; {currentYear} Kebab Palace. All rights reserved.</span>
              <div className="flex items-center space-x-4">
                <Link to="#" className="hover:text-accent transition-organic">Privacy Policy</Link>
                <span>•</span>
                <Link to="#" className="hover:text-accent transition-organic">Terms of Service</Link>
                <span>•</span>
                <Link to="#" className="hover:text-accent transition-organic">Accessibility</Link>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-white/60">
              <div className="flex items-center space-x-2">
                <Icon name="Star" size={16} className="text-accent fill-current" />
                <span>4.8/5 Rating</span>
              </div>
              <span>•</span>
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} className="text-accent" />
                <span>10,000+ Happy Customers</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;